import type { Test, Manifests, Manifest } from '../../common/api-client';
import React, { useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Panel,
  PanelHeader,
  PanelContent,
  ProgressBar,
  Icon
} from '@deque/cauldron-react';
import { Link } from 'react-router-dom';
import getGuides from '../../common/utils/get-guides';
import {
  getBaseFilters,
  getSearchParamsFromFilters
} from '../../common/hooks/useIssuesFilter';
import styles from './TestDetails.css';
import { useFeatureFlagState } from '../../common/contexts/featureFlags';
import type {
  UniversalIssueWithScreenshot,
  Filters
} from '@deque/axe-devtools-issues-utils';

interface TestOverviewProps {
  test: Test;
  issues: UniversalIssueWithScreenshot[];
  totalIssues: UniversalIssueWithScreenshot[];
  manifests: Manifests;
  filters: Filters;
}

interface IssueLinkProps extends React.HTMLAttributes<HTMLElement> {
  children: number;
  to: string;
}

const IssueLink = ({ children: count, ...props }: IssueLinkProps) => {
  return count > 0 ? <Link {...props}>{count}</Link> : <>{count}</>;
};

const TestOverview: React.FC<TestOverviewProps> = ({
  test,
  issues,
  totalIssues,
  manifests,
  filters
}) => {
  const { t } = useTranslation();
  const guides = getGuides(t);
  const enabledIGTs = guides.filter(guide => !guide.deprecated);
  const issueCounts = useMemo(
    () => ({
      automatic: totalIssues.filter(issue => !issue.isManual).length,
      guided: issues.filter(issue => issue.isManual && issue.igtId).length,
      needsReview: issues.filter(issue => issue.needsReview).length,
      bestPractice: issues.filter(issue =>
        issue.tags?.includes('best-practice')
      ).length,
      critical: issues.filter(
        issue => issue.impact === 'critical' && !issue.needsReview
      ).length,
      serious: issues.filter(
        issue => issue.impact === 'serious' && !issue.needsReview
      ).length,
      moderate: issues.filter(
        issue => issue.impact === 'moderate' && !issue.needsReview
      ).length,
      minor: issues.filter(
        issue => issue.impact === 'minor' && !issue.needsReview
      ).length,
      manual: totalIssues.filter(issue => issue.isManual && !issue.igtId).length
    }),
    [issues, totalIssues]
  );
  const igtCompletionCount = useMemo(() => {
    return enabledIGTs.reduce((count, igt) => {
      const runs: Manifest[] = manifests?.[igt.name] || [];

      if (test?.skipped_guides?.includes(igt.name)) {
        return count + 1;
      }

      const everyRunHasBeenCompleted =
        runs.every(
          (run: Manifest) =>
            (run?.body?.metadata as Record<string, unknown>)?.complete === true
        ) && runs.length > 0;

      return everyRunHasBeenCompleted ? count + 1 : count;
    }, 0);
  }, [manifests]);
  const igtProgress =
    igtCompletionCount > 0 && enabledIGTs.length > 0
      ? Number(((igtCompletionCount / enabledIGTs.length) * 100).toFixed(0))
      : 0;

  const manualTestsComplete = !!test.manual_tests_completed_at;
  const issuesLink = (newFilters?: Partial<Filters>) => {
    const search = getSearchParamsFromFilters({
      ...getBaseFilters(filters),
      ...newFilters
    });
    return `${location.pathname}/issues?${search}`;
  };

  const hasManualIssues = useFeatureFlagState('manual_issues_v2');

  return (
    <section className={styles.overview} aria-labelledby="test-overview">
      <h2 id="test-overview">{t('Test Record Overview')}</h2>
      <Panel>
        <PanelHeader>
          <h3 id="test-total-issues">{t('Total Issues')}</h3>
        </PanelHeader>
        <PanelContent>
          {totalIssues.length === 0 ? (
            <div className={styles.overview__total}>0</div>
          ) : (
            <Link
              className={styles.overview__total}
              to={issuesLink()}
              id="test-total-issues-link"
              aria-labelledby="test-total-issues-link test-total-issues"
            >
              {totalIssues.length}
            </Link>
          )}
        </PanelContent>
      </Panel>
      <Panel>
        <PanelHeader>
          <h3>{t`Total Issues By Category`}</h3>
        </PanelHeader>
        <PanelContent>
          <dl>
            <dt id="category-automatic">{t('Automatic')}</dt>
            <dd>
              <IssueLink
                to={issuesLink({ automatic: true })}
                id="category-automatic-link"
                aria-labelledby="category-automatic-link category-automatic"
              >
                {issueCounts.automatic}
              </IssueLink>
            </dd>
            <dt id="category-guided">{t('Guided')}</dt>
            <dd>
              <IssueLink
                to={issuesLink({ igt: 'all' })}
                id="category-guided-link"
                aria-labelledby="category-guided-link category-guided"
              >
                {issueCounts.guided}
              </IssueLink>
            </dd>
            {hasManualIssues && (
              <>
                <dt id="category-manual">{t('Manual')}</dt>
                <dd>
                  <IssueLink
                    to={issuesLink({ manual: true })}
                    id="category-manual-link"
                    aria-labelledby="category-manual-link category-manual"
                  >
                    {issueCounts.manual}
                  </IssueLink>
                </dd>
              </>
            )}
            {filters.configNeedsReview !== 'disabled' && (
              <>
                <dt id="category-needs-review">{t('Needs Review')}</dt>
                <dd>
                  <IssueLink
                    to={issuesLink({ needsReview: true })}
                    id="category-needs-review-link"
                    aria-labelledby="category-needs-review-link category-needs-review"
                  >
                    {issueCounts.needsReview}
                  </IssueLink>
                </dd>
              </>
            )}
            {filters.configBestPractices === 'enabled' && (
              <>
                <dt id="category-best-practice">{t('Best Practice')}</dt>
                <dd>
                  <IssueLink
                    to={issuesLink({ bestPractices: true })}
                    id="category-best-practice-link"
                    aria-labelledby="category-best-practice-link category-best-practice"
                  >
                    {issueCounts.bestPractice}
                  </IssueLink>
                </dd>
              </>
            )}
          </dl>
        </PanelContent>
      </Panel>
      <Panel>
        <PanelHeader>
          <h3>{t('Total Issues By Impact')}</h3>
        </PanelHeader>
        <PanelContent>
          <dl>
            <dt id="severity-critical">{t('Critical')}</dt>
            <dd>
              <IssueLink
                to={issuesLink({ impact: ['critical'] })}
                id="severity-critical-link"
                aria-labelledby="severity-critical-link severity-critical"
              >
                {issueCounts.critical}
              </IssueLink>
            </dd>
            <dt id="severity-serious">{t('Serious')}</dt>
            <dd>
              <IssueLink
                to={issuesLink({ impact: ['serious'] })}
                id="severity-serious-link"
                aria-labelledby="severity-serious-link severity-serious"
              >
                {issueCounts.serious}
              </IssueLink>
            </dd>
            <dt id="severity-moderate">{t('Moderate')}</dt>
            <dd>
              <IssueLink
                to={issuesLink({ impact: ['moderate'] })}
                id="severity-moderate-link"
                aria-labelledby="severity-moderate-link severity-moderate"
              >
                {issueCounts.moderate}
              </IssueLink>
            </dd>
            <dt id="severity-minor">{t('Minor')}</dt>
            <dd>
              <IssueLink
                to={issuesLink({ impact: ['minor'] })}
                id="severity-minor-link"
                aria-labelledby="severity-minor-link severity-minor"
              >
                {issueCounts.minor}
              </IssueLink>
            </dd>
          </dl>
        </PanelContent>
      </Panel>
      <Panel>
        <PanelHeader>
          <h3>{t('Testing Progress')}</h3>
        </PanelHeader>
        <PanelContent className={styles.testprogress}>
          <div className={styles.testprogress__label}>
            <h4 id="progress-automatic-testing">{t('Automatic Testing')}</h4>
            <span>100%</span>
          </div>
          <ProgressBar
            progress={100}
            aria-labelledby="progress-automatic-testing"
          />
          <div className={styles.testprogress__label}>
            <h4 id="progress-igt">{t('Intelligent Guided Testing')}</h4>
            <span>{igtProgress}%</span>
          </div>
          <ProgressBar
            progress={igtCompletionCount}
            progressMax={enabledIGTs.length}
            aria-labelledby="progress-igt"
          />
          <div className={styles.testprogress__label}>
            <h4 id="progress-remaining">
              <Link
                to="/coverage-page-state"
                target="_blank"
                rel="noopener noreferrer"
                aria-describedby="remaining-testing-description"
              >
                {t('Remaining Testing')}
                <Icon type="external-link" label={t('Open in new tab')} />
              </Link>
            </h4>
            <span>{manualTestsComplete ? '100%' : '0%'}</span>
          </div>
          <ProgressBar
            progress={manualTestsComplete ? 100 : 0}
            aria-labelledby="progress-remaining"
          />
          <p id="remaining-testing-description">
            <Trans>
              This represents the Manual Testing which{' '}
              <strong>Automatic and IGT testing does not cover.</strong>
            </Trans>
          </p>
        </PanelContent>
      </Panel>
    </section>
  );
};

export default TestOverview;
